// The knobs the decision is bounded by, and the lane matchers a repo's config
// compiles into. See `../babysit.ts` for the file docblock.

import { authorTrust, trustWithMembership } from '../../value-objects/author-trust';
import type { ProjectMembership } from '../../value-objects/auto-merge-scope';
import { compileGlob } from '../../value-objects/glob';
import type { MergeHold } from '../../value-objects/merge-hold';
import { ONBOARDING_BRANCH } from '../../value-objects/task-run-event';
import { TaskCancelReason } from '../task/cancel-reason';
import { projectFailureKinds, type TaskFailureKind } from '../task/failure-kind';
import { BabysitRoundCharge } from '../task/failure-kinds/row';
import { KNOWN_BOT_LOGINS } from './github-mappers';

// The round-charge vocabulary lives beside the failure-kind rows written in it.
export { BabysitRoundCharge } from '../task/failure-kinds/row';

// ─── policy ──────────────────────────────────────────────────────────────────

/**
 * Ceiling on re-attempts of ONE feedback action at ONE head — `address_comments`,
 * `address_findings` (retired), `reply_bot` (retired, plan 101 E11).
 *
 * These used to mint exactly once per `(repo, pr, head, action)`, because
 * `attemptFor` pinned their `attempt` to 1 and the follow-up id is a hash over that
 * tuple. So the FIRST terminal outcome — a cancel, a failed run, a box that died —
 * spent the id forever: the driver re-decided the same action every sweep, the insert
 * collided with the corpse, and the PR was pinned at that head with its feedback
 * permanently unaddressed. Measured on tesote/tesote.ai#1919 (2026-08-24), and before
 * it on #1876; the driver already documented the dead end and could only alert.
 *
 * Three, matching {@link DEFAULT_MAX_CI_FIX_ATTEMPTS} and the hard `attempt` bound in
 * `BabysitTaskSpecSchema` — a widened ladder here without moving that bound would
 * surface as a `mint_failed` log rather than a refusal.
 *
 * Head-scoped ON PURPOSE, unlike the CI and conflict counters: those bound a loop that
 * would otherwise re-push forever, while this one bounds retries of work on feedback
 * that is fixed in place. A new commit is new work and deserves a fresh ladder.
 */
export const DEFAULT_MAX_FEEDBACK_ATTEMPTS = 3;

/**
 * Default ceiling on CI self-fix attempts before escalating — stops an infinite red loop.
 * Not read on an AI-managed PR ({@link aiManagedPr}, owner ruling 2026-10-09): there the
 * convergence stop (`./convergence.ts`) is the bound, since it names the failure.
 */
export const DEFAULT_MAX_CI_FIX_ATTEMPTS = 3;

/**
 * Default ceiling on merge-conflict resolution attempts — stops an infinite
 * conflict→rebase→conflict loop. Read twice by `decideConflict` (`./conflict.ts`):
 * per (head, base) pair it escalates `merge_conflict` (#911); across heads, on the
 * resolve-run streak since the last evidence the PR got past its conflict, it escalates
 * `base_kept_moving` (#6849). The per-pair half alone does NOT end the loop: every
 * resolution mints a new head and restarts it, so a base that moves faster than a round
 * lands re-earned "attempt 1" for ever (developerz-ai/wurk#603). The failure the
 * cross-head read prevents is that loop — rounds whose pushes are stale on arrival —
 * not their cost.
 */
export const DEFAULT_MAX_CONFLICT_RESOLVE_ATTEMPTS = 3;

/**
 * Does a babysit round that ended with this failure kind spend its LADDER's budget?
 * ONE verdict per kind, read by all three ladders — CI self-fix, findings, conflict
 * resolve (`babysitRoundCharged`). The conflict ladder had its own three-kind list
 * (#4289) while the other two charged everything; tesote/tesote.ai#2501 and #2503
 * each spent their whole findings ladder in seconds on a BYOK key over its daily
 * limit (`provider_quota_exhausted` / `provider_auth_invalid`, 0 turns) and were
 * handed to a human as `findings_unresolved` — blaming work that never ran.
 *
 * THE RULE. `uncharged` when the kind says the provider, the account's key, the box
 * or delivery failed — the round never got to attempt the work, or lost it to
 * something outside it — AND a later round can succeed without anyone changing the
 * PR. `charged` when the work ran and failed, the model misbehaved, or the kind
 * cannot tell which. A DIFFERENT question from `TASK_FAILURE_BUDGET`
 * (`../task/failure-budget.ts`), which asks whether a TASK re-runs: a `pr_babysit`
 * follow-up never re-runs (a failure with its PR open is terminal on every arm,
 * `failureIsTerminalSql`), so this ladder IS its retry.
 *
 * THE "A LATER ROUND CAN SUCCEED" CLAUSE HAS AN EXCEPTION, and the kind's own row
 * says why: `pre_push_refused` (#7447) is UNCHARGED though it is NOT transient. OUR
 * land withheld the file the round created (#5452), so the round's work NEVER REACHED
 * the pull request — a refused land is not a head-unmoved fix attempt, and charging it
 * would spend a CI or findings rung on work that was never graded. A later round does
 * NOT succeed here: it re-derives the SAME brief, creates the same file OUTSIDE the
 * same exclusive file set, and the repository's own pre-push guard refuses the same
 * push. What bounds the uncharge is the `pre_push_refused` PARK — a `platform_fault`
 * with no rescue round (`./park-class.ts`), so the decision escalates it instead of
 * minting (`./decide.ts`). The machine re-arm re-runs it (`./machine-rearm.ts`, #7714):
 * at most `MACHINE_REARMS_PER_PR` per PR, never reset by a push, and the re-armed round's
 * land may widen its file set through the guard's proof.
 *
 * Projected from each kind's row (`babysitRound`, `../task/failure-kinds/`): the field is
 * required, so a new {@link TaskFailureKind} fails typecheck at its row until someone
 * decides whether it blames the work. Each kind's reasoning is on its row.
 *
 * WHAT BOUNDS AN UNCHARGED LOOP. An uncharged round still mints a row, so the
 * findings ladder is held by `max_sweeps_exceeded` (`sweepsSoFar >= prior rows`),
 * and the CI and conflict ladders by their own total-round ceiling at
 * `BabysitPolicy.maxSweeps` (`decide.ts`). Nothing here loops forever; a dead key
 * costs up to `maxSweeps` zero-turn rounds instead of three blamed ones.
 */
export const BABYSIT_ROUND_CHARGE: Readonly<Record<TaskFailureKind, BabysitRoundCharge>> =
  projectFailureKinds((row) => row.babysitRound);

/**
 * Does a round that ended with this `failureKind` spend its ladder? `null` — a row
 * still running, landed clean, or cancelled before it ever ran — charges: only a
 * kind this table names `uncharged` is excused. The ONE predicate all three babysit
 * ladders read ({@link BABYSIT_ROUND_CHARGE}).
 */
export function babysitRoundCharged(failureKind: TaskFailureKind | null): boolean {
  return failureKind === null || BABYSIT_ROUND_CHARGE[failureKind] === BabysitRoundCharge.Charged;
}

/**
 * Does a prior `resolve_conflict` follow-up spend the conflict-resolve budget
 * against a CURRENT (head sha, base sha) observation (#4289 owner decision)? Two
 * independent gates, both must hold for the row to count:
 *
 *  1. SAME CONFLICT. Keyed on the (head, base) PAIR, not the PR's lifetime: a base
 *     that moved (main merged again after a resolved conflict) or a head that moved
 *     (a new push) re-conflicts a DIFFERENT pair and earns a fresh budget.
 *     `prior.baseSha` is OPTIONAL — rows minted before it existed carry none — and a
 *     missing value counts as a MATCH: it fails closed toward the pre-#4289
 *     PR-lifetime count (the same direction `ciFixAttempts` already over-counts in,
 *     `apps/worker/src/babysit/wiring.ts`), so only a `baseSha` this predicate can
 *     actually read and compare may EXCLUDE a row from the budget. `current.baseSha`
 *     gets the SAME treatment: `live-read.ts`'s `PrSchema` parses an unreadable
 *     `base.sha` to `''` rather than throwing, and an empty CURRENT value is exactly as
 *     untrustworthy as a missing PRIOR one — comparing it for real equality would reset
 *     the budget on every degraded observation (GitHub answering with no `base.sha`
 *     resets three real charged attempts to zero, over and over). So `current.baseSha
 *     === ''` also counts as a MATCH, never as "a different pair."
 *     A fresh pair is fresh for THIS budget only: the cross-head streak (#6849,
 *     {@link conflictRoundCharged}) keeps counting, so a base that never stops moving
 *     still ends in `base_kept_moving`.
 *  2. CHARGED KIND. {@link babysitRoundCharged} — the one table every babysit
 *     ladder reads. Status-blind here, as before: the conflict count spans every
 *     status, so a live row with a stale uncharged kind is excused too.
 *
 * A row cancelled `superseded_by_rescue` (#6344) NEVER counts, whatever its kind: the
 * rescue round that retired it is the row's continuation, and charging the corpse would
 * spend the very budget the refile exists to give back. `prior.cancelReason` is OPTIONAL
 * — a caller that cannot read it leaves it out and the row charges, the fail-safe
 * direction, the same over-count a missing `baseSha` makes.
 */
export function conflictAttemptCounts(
  prior: {
    readonly headSha: string;
    readonly baseSha?: string | null | undefined;
    readonly failureKind: TaskFailureKind | null;
    readonly cancelReason?: TaskCancelReason | null | undefined;
  },
  current: { readonly headSha: string; readonly baseSha: string },
): boolean {
  if (!conflictRoundCharged(prior)) return false;
  const baseUnreadableOrAbsent =
    current.baseSha === '' || prior.baseSha === undefined || prior.baseSha === null;
  const samePair =
    prior.headSha === current.headSha &&
    (baseUnreadableOrAbsent || prior.baseSha === current.baseSha);
  return samePair;
}

/**
 * Rule 2 of {@link conflictAttemptCounts} alone — the CHARGED half, blind to the pair: a
 * `superseded_by_rescue` corpse never charges, every other row charges by
 * {@link babysitRoundCharged}. The cross-head streak (#6849, `conflictResolveStreakFor`
 * in `apps/worker/src/babysit/ladder-counts.ts`) reads this too, with ONE divergence: a
 * row that ended `base_moved` is excused here (uncharged on the task) and COUNTED by the
 * streak, once per re-run, because a base that outran the resolution is the very failure
 * the streak bounds.
 */
export function conflictRoundCharged(prior: {
  readonly failureKind: TaskFailureKind | null;
  readonly cancelReason?: TaskCancelReason | null | undefined;
}): boolean {
  if (prior.cancelReason === TaskCancelReason.SupersededByRescue) return false;
  return babysitRoundCharged(prior.failureKind);
}

/**
 * Default ceiling on TOTAL babysit sweeps before escalating `max_sweeps_exceeded`
 * (#581). Backstops the non-`fix_ci` follow-up actions (`address_comments` /
 * `address_findings`; `reply_bot` retired by plan 101 E11): with human/finding address-once a handled
 * item drops out, but a pathological stream of new feedback (or a self-fix push
 * that keeps re-triggering CI/bots) must still hand off to a human eventually
 * rather than sweep forever.
 *
 * It also caps the CI and conflict ladders' TOTAL rounds, charged plus excused
 * ({@link BABYSIT_ROUND_CHARGE}): an excused round spends no budget but mints a row,
 * so without this a key that never recovers re-mints every sweep (`decide.ts`).
 */
export const DEFAULT_MAX_BABYSIT_SWEEPS = 20;

/**
 * Default ceiling on consecutive `github_refused` merge refusals (GitHub answered
 * `merged: false` on a sha-bound PUT) for the SAME head SHA before escalating
 * `github_refused_stuck` (#1532). A single refusal is a legitimate push race — a
 * commit landing between the read and the sha-bound write, which loses safely and
 * resolves on the next sweep — so the bound is > 1. Three mirrors the
 * {@link DEFAULT_MAX_CI_FIX_ATTEMPTS} retry-before-escalate convention.
 */
export const DEFAULT_MAX_GITHUB_REFUSED_ATTEMPTS = 3;

/**
 * The merge-gate inputs babysit reads, distilled from repo policy (`packages/policy`
 * `pr`). Kept as a local type so domain need not depend on `@developerz/policy`; the
 * `Pr → BabysitPolicy` adapter lives with the driver (which already deps policy).
 */
/**
 * Why our review lane will not review this repo's PRs (#7478 B) — the switch that turned it
 * off and which config source drove it (`EffectiveReviewGate.source`).
 */
export type ReviewLaneOff = {
  readonly kind: 'review_disabled' | 'auto_review_off';
  readonly source: 'native' | 'coderabbit' | 'default';
};

export type BabysitPolicy = {
  /** `pr.auto_merge` — merge automatically when green, else escalate to a human. */
  readonly autoMerge: boolean;
  /**
   * The platform-side merge hold (#7011): a person held this repo's merges for review,
   * or `null` when nobody did. The seam (`resolvePolicy`) already folded it into
   * `autoMerge` (a hold forces it off); it rides here too so the decision parks a held
   * repo as `merge_held`, whose remedy is clearing the hold, rather than
   * `auto_merge_disabled`, whose remedy is a `.maintainer.yml` edit. Named `mergeHold`,
   * not `hold`: in `decideBabysit` a bare `hold` is the human-merge lane's match.
   */
  readonly mergeHold: MergeHold | null;
  /** `pr.required_checks` — branch-protection checks that MUST be present + passing to merge. */
  readonly requiredChecks: readonly string[];
  /**
   * `ci.self_fix` — may this watch answer a red check by PUSHING A COMMIT at it
   * (#1259)? The REACTION half of the CI lane, and the half `ci.enabled` never
   * governed: that key gates whether the fleet PLACES a repo's CI jobs on our
   * boxes (`apps/worker/src/dispatch/ci-assigner.ts`, its only reader) and is
   * invisible here, so a repo that moved its CI to an external runner and set
   * `enabled: false` still had our agent pushing commits at failures its own
   * engineers own.
   *
   * OFF changes only the reaction: the failure is still observed, the PR is still
   * held (`awaiting_ci`), and the merge gate still refuses a red head — an
   * external-CI repo wants us to wait for ITS green just as much as for ours. What
   * stops is the `fix_ci` follow-up and, with it, `ci_unfixable` (a budget nobody
   * spent cannot have been exhausted); a watch stuck red drives
   * `ci_self_fix_disabled` instead.
   *
   * REQUIRED, like every other field here. It decides whether the platform writes
   * to a customer's branch, so a constructor that omitted it would be choosing that
   * silently; the adapter (`babysitPolicyFromPr`) reads it off the parsed policy,
   * where the schema default supplies it.
   */
  readonly selfFixCi: boolean;
  /** Ceiling on CI self-fix attempts before escalating. */
  readonly maxCiFixAttempts: number;
  /** Ceiling on merge-conflict resolution attempts before escalating `merge_conflict` (#911). */
  readonly maxConflictResolveAttempts: number;
  /** Ceiling on TOTAL sweeps before escalating `max_sweeps_exceeded` (#581). */
  readonly maxSweeps: number;
  /**
   * Ceiling on consecutive `github_refused` merge refusals for the SAME head SHA
   * before escalating `github_refused_stuck` (#1532). Mirrors {@link maxCiFixAttempts}:
   * the bound on a retry loop before a human takes it. The counter is derived per
   * hop from the audit history keyed on head SHA, so a new push resets it.
   */
  readonly maxGithubRefusedAttempts: number;
  /**
   * `pr.require_review` — may a head NOBODY reviewed be merged (#1080)? ON by
   * default: the advertised gate is "CI green + review + branch protections", and a
   * repo with no external review requirement and no verdict of ours would otherwise
   * merge on nothing but a 2-minute commit-age grace. When on, review-absence WAITS
   * (a verdict may still land) and then escalates `review_absent` rather than
   * merging — a human is told, the PR is never merged unreviewed.
   *
   * Turning it OFF restores the pre-#1080 behaviour for a repo that deliberately
   * ships machine-gated on CI alone: merge once the head has outlived
   * {@link REVIEW_SETTLEMENT_MS}. It is NOT an escape from the undateable-head
   * bound — a grace that cannot be proved elapsed still escalates rather than
   * merging or waiting forever.
   *
   * Only ever consulted where the alternative is a MERGE: an `autoMerge: false`
   * repo hands every PR to a human anyway.
   */
  readonly requireReview: boolean;
  /**
   * Our review lane STOOD DOWN by this repo's own config (#7478 B) — `null` when it reviews.
   * Read from the review lane's ONE effective gate (`apps/worker` `review/effective-gate.ts`:
   * native `review:` > translated `.coderabbit.yaml` > defaults), the same read its
   * `planReview` refuses `review_disabled` / `auto_review_off` on, so babysit and the lane
   * cannot disagree about whether a verdict of ours can ever land. With it set, a
   * {@link requireReview} hold waits for a member's approval with no clock: no wait bound
   * can end it, so it never escalates `review_absent` (`./review-absent.ts`).
   */
  readonly reviewLaneOff: ReviewLaneOff | null;
  /**
   * `pr.human_merge` — the HUMAN-MERGE lane (#840): globs whose match holds a PR
   * for a human EVEN WHEN `autoMerge` is on. Plain string arrays, not the policy
   * type, so domain still imports nothing (`babysitPolicyFromPr` adapts it).
   * Both empty (the default) = no lane, and the merge gate is `autoMerge` alone.
   */
  readonly humanMerge: HumanMergeLane;
  /**
   * `escalate.ask_before_acting` as the repo's resolved policy carries it (plan 101 M3).
   * The decision grades the two categories it can prove from the observation alone —
   * `first_time_contributor` and `major_dep_bump` ({@link classifyAskBeforeActing}) —
   * and parks a merge `escalated` when one matches a listed category. `large_pr` is
   * proven from the PR's line count, which the merge egress reads and the observation
   * does not carry yet, so the egress rung enforces it (01-5). `breaking_change` needs
   * judgment and stays with the PR-loop session's prompt. Plain strings, like
   * {@link humanMerge}: domain imports no policy vocabulary.
   */
  readonly askBeforeActing: readonly string[];
  /**
   * The `escalate.ask_before_acting` categories the repo's committed yml WROTE
   * (`askBeforeActingWritten`, `@developerz/policy`) — never the schema default. The list an
   * AI-managed PR ({@link aiManagedPr}) is graded against (owner ruling 2026-10-09): the
   * default holds every other author's PR, never the platform's own or a dependency bot's.
   */
  readonly askBeforeActingWritten: readonly string[];
  /**
   * Has the repo given its ONBOARDING CONSENT (#7033, #7540)? `true` when the repo has an
   * active snapshot OR the account posture is maintain (onboarding consent; plan 102 slice
   * 06, decided in `apps/worker/src/babysit/policy-load.ts`). An active valid snapshot's
   * only writer is `policy.ingest`, fed by a default-branch push of
   * `.dz/maintainer/maintainer.yml` / `.maintainer.yml` or the repair lane's re-read of the
   * default branch, and a deleted or invalid file clears it; a `maintain` posture is the
   * owner's consent for every repo of the account. `false` is a `standard` account's repo
   * whose base has no valid file — a first onboarding, still pinned. Read by the
   * `dz/onboarding` pin only ({@link matchHumanMergeHold}).
   */
  readonly basePolicyCommitted: boolean;
};

/**
 * The dependency-update bots whose PR titles state the version bump — the dependabot and
 * renovate kinds of the one bot table (11-11), so a self-hosted `renovate-bot` is one.
 */
const DEP_BOT_LOGINS: ReadonlySet<string> = new Set([
  ...KNOWN_BOT_LOGINS.dependabot,
  ...KNOWN_BOT_LOGINS.renovate,
]);

/**
 * Dependabot: `Bump <dep> from 1.2.3 to 2.0.0` (any prefix, any case). Captures the
 * numeric `major[.minor[.patch]]` of each side; a pre-release/build tail is ignored.
 */
const DEPENDABOT_BUMP =
  /\bfrom\s+v?(\d+(?:\.\d+){0,2})[\w.+-]*\s+to\s+v?(\d+(?:\.\d+){0,2})[\w.+-]*/i;

/**
 * Is `from → to` a BREAKING upgrade under semver? The leftmost NON-ZERO component
 * of `from` is its major: `1.2.3 → 2.0.0`, `0.9.0 → 0.10.0` and `0.0.3 → 0.0.4` all
 * break, `1.9.0 → 1.10.0` and `0.9.1 → 0.9.2` do not. Comparing only the first
 * component missed every 0.x break (plan 101/04 row 5). A downgrade is not a bump.
 */
function isBreakingBump(from: string, to: string): boolean {
  const a = from.split('.').map(Number);
  const b = to.split('.').map(Number);
  for (let i = 0; i < 3; i += 1) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return y > x;
    if (x !== 0) return false;
  }
  return false;
}
/**
 * Renovate: a major update's title names ONLY the new major (`… to v19`) — its default
 * template renders `to {{prettyNewMajor}}` for a major and the full version otherwise —
 * and a `(major)` suffix where the repo's config adds one.
 */
const RENOVATE_MAJOR = /\bto\s+v\d+\s*(?:\(major\))?\s*$|\(major\)\s*$/i;

/**
 * One bumped package in a dependency bot's PR BODY, where the title states none — a
 * GROUPED update (`Bump the minor-and-patch group with 3 updates`):
 *
 *   - Dependabot's per-package line, ``Updates `jsdom` from 30.1.1 to 30.1.2``;
 *   - Dependabot's table for a larger group, ``| [jsdom](…) | `30.1.1` | `30.1.2` |``;
 *   - Renovate's table, ``| [jsdom](…) | `30.1.1` -> `31.0.0` |`` (its range operators and
 *     the `→` arrow included).
 *
 * Captures (name, from, to) of each. A group that hides one major among patches was
 * invisible to the title-only classifier, so a repo that LISTED `major_dep_bump` merged it.
 * Every pattern is anchored to one line and matches no newline, so the scan is linear in
 * the body (GitHub caps a PR body at 65,536 characters).
 */
const BODY_BUMPS: readonly RegExp[] = [
  /^Updates[ \t]+`([^`\n]+)`[ \t]+from[ \t]+v?(\d+(?:\.\d+){0,2})[\w.+-]*[ \t]+to[ \t]+v?(\d+(?:\.\d+){0,2})/gim,
  /^\|[ \t]*\[?([^\]|\n]+?)\]?(?:\([^)\n]*\))?[ \t]*\|[ \t]*`?[\^~>=< \t]*v?(\d+(?:\.\d+){0,2})[\w.+-]*`?[ \t]*(?:\||->|→)[ \t]*`?[\^~>=< \t]*v?(\d+(?:\.\d+){0,2})/gim,
];

/** The first semver-breaking bump a dependency bot's PR body lists, or `null`. Pure. */
export function breakingBumpInBody(
  body: string,
): { readonly name: string; readonly from: string; readonly to: string } | null {
  for (const pattern of BODY_BUMPS) {
    for (const m of body.matchAll(pattern)) {
      const [, name = '', from = '', to = ''] = m;
      if (isBreakingBump(from, to)) return { name: name.trim(), from, to };
    }
  }
  return null;
}

/**
 * Is this pull request AI-MANAGED — opened by our own App or by a dependency bot
 * (Dependabot, Renovate) on a repo that auto-merges (owner ruling 2026-10-09)? On such a PR
 * no count budget ends in a human ask, and neither does a hold of our OWN reviewer's
 * (`./decide.ts`): CI, our review and `guardedMerge` gate it, and the platform keeps working
 * it. Holds an external party owns (a human's or another bot's change request, a hold the
 * repo's yml declares) still stand.
 *
 * The author-scope floor is not re-graded here: a dependency bot's PR under a
 * `pr.auto_merge_scope` that excludes it is let go before the decision (`outOfScopeWatch`,
 * `apps/worker`) and refused at the egress (`author_out_of_scope`). A fleet PR opened under a
 * member's own token (a `user_token` binding) carries that member's login, so it reads as
 * theirs here.
 */
export function aiManagedPr(
  obs: { readonly author: { readonly login: string; readonly isOurs: boolean } },
  policy: Pick<BabysitPolicy, 'autoMerge'>,
): boolean {
  return policy.autoMerge && aiManagedAuthor(obs.author);
}

/**
 * The author half of {@link aiManagedPr}: our own App, or a dependency bot. The merge
 * egress asks it on the LIVE author, where a merge by us already implies auto-merge.
 */
export function aiManagedAuthor(author: {
  readonly login: string;
  readonly isOurs: boolean;
}): boolean {
  return author.isOurs || DEP_BOT_LOGINS.has(author.login.toLowerCase());
}

/**
 * A pull request author's membership in the ORGANIZATION that owns the repo, read live for
 * {@link orgMemberPr}: `member` is an ACTIVE membership with role admin or member;
 * `not_member` is none, a pending invitation, a billing manager, a bot, or a repo owned by a
 * personal account (no organization); `unknown` is a read that failed or answered a shape
 * nobody documented, or no read at all (a fork, where it cannot matter, or a PR read that
 * named no organization owner or no author). Not a repo permission: an outside collaborator
 * with write is `not_member` here.
 */
export type OrgMembership = 'member' | 'not_member' | 'unknown';

/** What {@link orgMemberPr} reads off the live pull request. */
export type OrgMemberPrFacts = {
  readonly authorIsBot: boolean;
  /** The head branch lives in the base repository: `false` for a fork, or a deleted one. */
  readonly sameRepo: boolean;
  readonly orgMembership: OrgMembership;
};

/**
 * Did an ACTIVE MEMBER of the repo's owning organization open this pull request from a
 * branch of the repo itself (owner ruling 2026-10-22, reversing 07-8 for org members)? The
 * babysit driver answers such a PR's `stale_base` merge refusal by updating its branch, as
 * it does a fleet or {@link aiManagedPr} one (`apps/worker` `answerStaleBase`). Never a fork
 * (the branch is the contributor's repository), never an outside collaborator (a repo
 * permission is not membership), never an unread membership (fail closed). Pure.
 *
 * Kept apart from {@link aiManagedPr} on purpose: that verdict lifts count budgets and our
 * own reviewer's holds across the decision core, and a member's PR earns none of that —
 * only the branch update.
 */
export function orgMemberPr(facts: OrgMemberPrFacts): boolean {
  return !facts.authorIsBot && facts.sameRepo && facts.orgMembership === 'member';
}

/**
 * {@link aiManagedAuthor} as a list of lower-cased logins, for a read that filters on the
 * stored author (`pull_requests.author_login`, the stuck-PR read, #6338): `ourBotLogin`
 * (`requireOurBotLogin()`) plus the dependency bots. A login is in it exactly when
 * `aiManagedAuthor` admits it with `isOurs` derived as the observation derives it
 * (case-insensitive equality with our App's login); `stuck-pr.test.ts` pins the kept
 * set over every author shape.
 */
export function aiManagedAuthorLogins(ourBotLogin: string): readonly string[] {
  return [ourBotLogin.toLowerCase(), ...DEP_BOT_LOGINS];
}

/**
 * The `ask_before_acting` list a PR is graded against: the categories the repo WROTE for
 * an AI-managed PR, the resolved list (schema default included) for every other
 * (owner ruling 2026-10-09). ONE rule for the decision and the merge egress.
 */
export function askBeforeActingFor(
  managed: boolean,
  lists: { readonly listed: readonly string[]; readonly written: readonly string[] },
): readonly string[] {
  return managed ? lists.written : lists.listed;
}

/**
 * {@link askBeforeActingFor} for one observed PR: its author graded by {@link aiManagedPr}.
 * The decision core's one reading of "which categories does this PR ask for".
 */
export function askListForPr(
  obs: { readonly author: { readonly login: string; readonly isOurs: boolean } },
  policy: Pick<BabysitPolicy, 'autoMerge' | 'askBeforeActing' | 'askBeforeActingWritten'>,
): readonly string[] {
  return askBeforeActingFor(aiManagedPr(obs, policy), {
    listed: policy.askBeforeActing,
    written: policy.askBeforeActingWritten,
  });
}

/** The categories {@link classifyAskBeforeActing} can prove without a model. */
export type ProvableAskCategory = 'first_time_contributor' | 'major_dep_bump' | 'large_pr';

/**
 * The PR's size against `pr.large_threshold_lines` (01-5). `changedLines` is GitHub's
 * `additions + deletions`, `null` when the payload carried neither — which FAILS CLOSED,
 * like an unknown association does: a gate whose job is "a maintainer looks at this one"
 * holds when it cannot tell.
 */
export type AskBeforeActingSize = {
  readonly changedLines: number | null;
  readonly thresholdLines: number;
};

/**
 * The first LISTED `ask_before_acting` category the PR PROVES, if any (plan 101 M3). Pure
 * and deterministic, so it runs where the alternative is a merge by us and never needs a
 * model. Returns the category with its evidence (for the audit reason), or null.
 *
 * Intersects with `listed` itself rather than proving one category and letting the
 * caller intersect: the size category and an identity category can both hold on one PR,
 * and a proven-but-unlisted one must not shadow a listed one.
 *
 *   - `first_time_contributor`: an author {@link authorTrust} reads as `first_time` —
 *     FIRST_TIME_CONTRIBUTOR / FIRST_TIMER / NONE, or an unknown association (fails
 *     closed). Bots are excluded: GitHub reports `NONE` for an App's PRs too, our own
 *     fleet's included.
 *   - `major_dep_bump`: a Dependabot/Renovate PR whose title states a major bump — a
 *     semver-breaking one, so a 0.x minor bump counts ({@link isBreakingBump}). A
 *     title that states no versions (a grouped update) is graded on the version list
 *     the bot writes in the PR BODY ({@link breakingBumpInBody}) when the caller holds
 *     it — the merge egress does, off the live PR; a caller with no body cannot prove
 *     a group and does not classify it.
 *   - `large_pr`: more changed lines than the threshold ({@link AskBeforeActingSize}).
 *     `size: null` is a caller that holds no line count (the babysit observation today);
 *     the merge egress always passes one.
 */
export function classifyAskBeforeActing(
  obs: {
    readonly author: {
      readonly login: string;
      readonly isBot: boolean;
      readonly association: string | null;
      /** The repo-permission verdict when the caller read one (#6359); only `member` acts. */
      readonly membership?: ProjectMembership | undefined;
    };
    readonly title: string;
    readonly body?: string | null | undefined;
  },
  listed: readonly string[],
  size: AskBeforeActingSize | null,
): { readonly category: ProvableAskCategory; readonly evidence: string } | null {
  const identity = classifyAuthor(obs);
  if (identity !== null && listed.includes(identity.category)) return identity;
  if (size === null || !listed.includes('large_pr')) return null;
  if (size.changedLines === null) {
    return { category: 'large_pr', evidence: 'changed-line count unreadable' };
  }
  if (size.changedLines > size.thresholdLines) {
    return {
      category: 'large_pr',
      evidence: `${size.changedLines} changed lines > large_threshold_lines ${size.thresholdLines}`,
    };
  }
  return null;
}

/** The identity half: who opened the PR, and what a dependency bot's title states. */
function classifyAuthor(obs: {
  readonly author: {
    readonly login: string;
    readonly isBot: boolean;
    readonly association: string | null;
    readonly membership?: ProjectMembership | undefined;
  };
  readonly title: string;
  readonly body?: string | null | undefined;
}): {
  readonly category: 'first_time_contributor' | 'major_dep_bump';
  readonly evidence: string;
} | null {
  const login = obs.author.login.toLowerCase();
  if (DEP_BOT_LOGINS.has(login)) {
    const dependabot = DEPENDABOT_BUMP.exec(obs.title);
    if (dependabot !== null && isBreakingBump(dependabot[1] ?? '', dependabot[2] ?? '')) {
      return { category: 'major_dep_bump', evidence: `${login}: "${obs.title}"` };
    }
    if (dependabot === null && RENOVATE_MAJOR.test(obs.title.trim())) {
      return { category: 'major_dep_bump', evidence: `${login}: "${obs.title}"` };
    }
    // A title with no versions is a group: the body's list is the only statement of them.
    const grouped = dependabot === null && obs.body ? breakingBumpInBody(obs.body) : null;
    if (grouped !== null) {
      return {
        category: 'major_dep_bump',
        evidence: `${login}: "${obs.title}" includes ${grouped.name} ${grouped.from} → ${grouped.to}`,
      };
    }
    return null;
  }
  // The identity half is THE author-trust classifier's `first_time` rung, which fails
  // closed on an unknown association (plan 101 PR1). `ourBotLogin: null`: this seat
  // holds no platform identity, and our App reading as `bot` is a non-ask either way.
  // A definite repo-permission `member` is never a first-timer (#6359); `unknown` and
  // `not_member` keep the association grade.
  const trust = trustWithMembership(
    authorTrust({
      login: obs.author.login,
      type: obs.author.isBot ? 'Bot' : undefined,
      association: obs.author.association,
      ourBotLogin: null,
    }),
    obs.author.membership,
  );
  if (trust !== 'first_time') return null;
  const association = obs.author.association?.trim().toUpperCase() || 'unknown';
  return {
    category: 'first_time_contributor',
    evidence: `${obs.author.login} (author_association ${association})`,
  };
}

/** The two glob lists `pr.human_merge` carries: PR target branches, and touched paths. */
export type HumanMergeLane = {
  readonly paths: readonly string[];
  readonly branches: readonly string[];
};

/** No lane — the default every non-configuring repo gets. */
export const NO_HUMAN_MERGE_LANE: HumanMergeLane = { paths: [], branches: [] };

/**
 * Does this PR fall in the repo's HUMAN-MERGE lane (#840)? Returns the PATTERN
 * that matched (for the audit reason) or null. Pure — the globs compile through
 * the one shared dialect ({@link compileGlob}), so a lane's `db/migrations/**`
 * and a review path filter can never mean different things.
 *
 * FAILS CLOSED on unknown paths: a configured `paths` lane with `changedPaths:
 * null` (never fetched, or a file list too large to read in full) matches. The
 * safe direction for a lane whose whole job is "a human looks at this one" is to
 * hold, not to merge on a list we could not read. A branch lane needs no such
 * guard — `baseRef` always comes off the PR fetch.
 */
export function matchHumanMergeLane(
  lane: HumanMergeLane,
  pr: { readonly baseRef: string; readonly changedPaths: readonly string[] | null },
): string | null {
  for (const glob of lane.branches) {
    if (compileGlob(glob).test(pr.baseRef)) return `branches:${glob}`;
  }
  if (lane.paths.length === 0) return null;
  if (pr.changedPaths === null) return `paths:${lane.paths[0]} (changed files unknown)`;
  for (const glob of lane.paths) {
    const re = compileGlob(glob);
    if (pr.changedPaths.some((p) => re.test(p))) return `paths:${glob}`;
  }
  return null;
}

/**
 * Is this pull request a HUMAN's to merge? Returns the reason that held it (for
 * the audit) or null. Two things can hold it and both answer here, because both
 * end in the same act — hand the PR over instead of merging it:
 *
 *   - the repo's own configured lane ({@link matchHumanMergeLane}, #840);
 *   - the platform's own pin on `dz/onboarding` (arc 101 slice 05 G5).
 *
 * This is the question the two MERGE gates ask — the decision core below and the
 * egress that performs the PUT. {@link matchHumanMergeLane} stays the narrower
 * one, "did the repo's CONFIG cover this PR", which is the fact the self-merge
 * audit row records (`humanMergeLaneMatched`) and must not start reporting a pin
 * the maintainer never wrote.
 *
 * WHY THE PIN HOLDS A FIRST ONBOARDING UNCONDITIONALLY. Merging the onboarding PR is what stamps
 * `repositories.ai_first_at` and releases every coding task the claim gate holds
 * on that repo, and its diff rewrites the repo's own `CLAUDE.md`, `bin/` trio and
 * `.claude/` surface — an OUTWARD act on someone else's repository, the same
 * class `defaultPolicy`'s `release.manager: none` refuses to take unasked. A
 * DEFAULT would not pin it: `pr.auto_merge` arrives here `true` on any repo that
 * ships a `.maintainer.yml` omitting the key (the schema default, #101) on any
 * tier `enforceAutoMergeTierGate` does not cover — which is the common case, not
 * the exotic one. So the pin is a conjunct, not a default.
 *
 * KEYED ON THE HEAD REF, never the base: the pass always cuts the same constant
 * branch ({@link ONBOARDING_BRANCH} — that IS its idempotency key), while the
 * base is whatever the repo's default branch happens to be.
 *
 * `approvedHead`: a maintainer's `auto` covers THIS head (`pr approve_pending`,
 * pinned to the head it approved). The lane asks a human to DECIDE the merge and
 * that approval is the decision, so it releases the lane (tesote/tesote.ai#2790,
 * where the only other way out was the GitHub button). It never releases the pin:
 * the onboarding merge is itself the maintainer's consent.
 *
 * `basePolicyCommitted` ({@link BabysitPolicy.basePolicyCommitted}, REQUIRED so every merge
 * gate states it): the pin is that consent only while the repo's default branch has NO
 * committed policy file. A yml committed on the default branch — by hand, or by a merged
 * onboarding PR — IS the consent, the same fact the non-negotiable "auto-merge by default
 * once a repo ships `.dz/maintainer/maintainer.yml`" keys on. Once it is there, a refreshed
 * `dz/onboarding` PR (the setup pass re-cutting the same branch) is an ordinary PR under
 * that policy — tesote/treasury#9067 sat pinned forever on a repo whose main carried
 * `auto_merge: true` (#7033). The repo's own lane still holds it.
 * The account's `maintain` posture also lifts the pin: it is the same consent (#7540).
 *
 * WHO CAN SET THE FACT, enumerated (2026-10-23): the active pointer has ONE production
 * writer, `apps/worker` `policy.ingest` (`policy-ingest.lane.ts`, `save` and `clear`). Its
 * two producers both read the DEFAULT branch: a default-branch push touching either policy
 * path (`dispatch/push-fanout.ts` `policyChangeFromPush`), and the repair lane
 * (`consumers/policy-repair.lane.ts`), which sends no `sourceSha`, so ingest reads the
 * default branch. A deleted file clears the pointer and an invalid one invalidates it.
 * `packages/db` seeds and `packages/testing` policy seeding write it too, dev and test only.
 * The SECOND source lifts the pin by posture, not by file (plan 102 slice 06, #7540):
 * `accounts.maintain_posture`, whose one production writer is `setAccountPosture`
 * (`packages/db`), reached through `apps/api/src/deps/account-posture.ts` from two
 * producers, `PUT /v1/accounts/:id/posture` (`routes/account-posture.ts`) and the MCP
 * `account posture_set` action, both audited `account.posture_changed`.
 */
export function matchHumanMergeHold(
  lane: HumanMergeLane,
  pr: {
    readonly baseRef: string;
    readonly headRef: string;
    readonly changedPaths: readonly string[] | null;
  },
  approvedHead: boolean,
  basePolicyCommitted: boolean,
): string | null {
  return (
    onboardingPinHolds(pr.headRef, basePolicyCommitted) ??
    (approvedHead ? null : matchHumanMergeLane(lane, pr))
  );
}

/**
 * The `dz/onboarding` pin as the MERGE gates apply it: {@link matchOnboardingPin}, and only
 * while the base carries no committed policy file (see {@link matchHumanMergeHold}).
 */
export function onboardingPinHolds(
  headRef: string | null,
  basePolicyCommitted: boolean,
): string | null {
  return basePolicyCommitted ? null : matchOnboardingPin(headRef);
}

/**
 * The pin half of {@link matchHumanMergeHold}, on its own, for the seam that
 * holds a head ref and no lane to match it against (`resolveHumanMergeLane` in
 * `@developerz/policy`, the agent-session capability gate's resolver). ONE
 * predicate, so the marker string and the branch it names cannot drift apart.
 *
 * `null` — an unknown head ref — does NOT pin, and that asymmetry against a
 * configured `branches` lane is deliberate. A lane exists to hold a CLASS of pull
 * requests, so "we could not tell" must hold; this names exactly ONE branch, and
 * reading unknown as that branch would hold every PR whose delivery carried no PR
 * body. The gate that MERGES never sees an unknown head anyway — it grades
 * `head.ref` off its own PR fetch, in the decision and again in the egress.
 */
export function matchOnboardingPin(headRef: string | null): string | null {
  return headRef === ONBOARDING_BRANCH ? `onboarding:${ONBOARDING_BRANCH}` : null;
}
